/**
 * THE SPOKEN DIGIT READER — plan-029 PC-DOM (design §40.9.5, §40.9.8, §40.20.1; the spoken-code research §4, §6.2; ADR
 * 0082, proposed; glossary §6.44 *Capture window*; D-10).
 *
 * `spokenDigitSequenceOf(text, language, wanted)` reads the digits of an utterance a party said as a confirmation code
 * or a passcode — never a value: "forty-two" is the two digits 4 and 2, "double five" is 5 and 5. One grammar for el, en,
 * es and pt over the survey's word tables (`../template/survey/number-words.ts`, shared, not copied):
 *
 * ```
 * unit                        reads as                       examples
 * numeral run                 its digits, literally          482, 04, 913 ("482.913", "48-29-13": separators are not data)
 * digit word 0–9              one digit                      one, μηδέν, cero, zero (en also: oh, o, nought, naught)
 * teen / two-digit word       two digits                     thirteen 13, δεκατρία 13, quince 15, veintitrés 23
 * tens word                   T0, or Td with a digit word    forty-two 42 (or 40 2); σαράντα δύο; treinta y dos
 * hundreds                    H00, or H + a 1–2 digit rest   four hundred 400; four hundred five 405; τετρακόσια ογδόντα δύο 482
 * repeat word + digit word    the digit twice / three times  double five 55, triple zero 000 (en, es only)
 * joiner (and, και, y, e)     nothing                        skipped inside a run, trimmed at its edges
 * thousands word              no parse (ambiguous)           a code said as "four thousand …" has lost its zeros
 * article (a, an; μια, μιαν,  not a digit                    "a code", "μια στιγμή", "un código", "uma vez"
 *  ενας; un, una; uma)
 * ```
 *
 * Every parse is enumerated, never chosen: exactly one distinct reading of the wanted length is `exact`; two are
 * `ambiguous`; only shorter ones `short`; only longer ones `long`. **One run only**: two separate digit runs in one text
 * ("four eight two, uh, sorry, nine one three") are `ambiguous` — a reading is never assembled from two runs, so the
 * reader returns at most one candidate per attempt and the guess arithmetic of §40.15.1 holds.
 *
 * **A lone number word is ordinary speech.** A run of one number word ("one moment please", "I'd like two", "τετρακόσια")
 * is not a digit run: it reads `none` and is not redacted. A single word holds at most three digits, below every code and
 * passcode length (4–8), and the capture window releases such words anyway (`…_MINIMUM_DIGIT_TO_HOLD`, research §6.2). A
 * numeral is always a run ("12"). Over-redaction is the safe direction elsewhere: every digit run (not only the first)
 * is in `everySpan`, which `redactedOf` replaces.
 *
 * Pure: no clock, no I/O. Never logs; `unknownToken` is for the offline harness's gap report only.
 */
import { normalSpoken, numberWordsOf, primarySubtagOf } from "../template/survey/number-words.js";
/** Words the tables read as one that are articles in the language, never a digit (research §4.2, F11). */
export const SPOKEN_DIGIT_ARTICLE = {
    en: ["a", "an"],
    el: ["μια", "μιαν", "ενασ"],
    es: ["un", "una"],
    pt: ["uma"],
};
/** Zero's other names, English only ("o" is the Portuguese article and Spanish "or"). */
export const SPOKEN_DIGIT_ZERO_ALIAS = {
    en: ["oh", "o", "nought", "naught"],
    el: [],
    es: [],
    pt: [],
};
/**
 * Repeat words, per language, enabled where the form is said (research §4.2, H5: confirmed per language by the harness
 * and a native speaker; Greek and Portuguese ship with none).
 */
export const SPOKEN_DIGIT_REPEAT_WORD = {
    en: { double: 2, triple: 3 },
    el: {},
    es: { doble: 2, triple: 3 },
    pt: {},
};
/** Distinct partial readings kept per position; a run that fans out further is `ambiguous`, never guessed. */
const CANDIDATE_MAXIMUM = 16;
function piecesOf(text) {
    const found = [];
    for (const match of text.matchAll(/[\p{L}\p{N}]+/gu)) {
        found.push({ normal: normalSpoken(match[0]), start: match.index, end: match.index + match[0].length });
    }
    return found;
}
const NONE = Object.freeze({
    reading: Object.freeze({ kind: "none" }),
    span: null,
    everySpan: Object.freeze([]),
    unknownToken: Object.freeze([]),
});
/** Reads the digits a party said (see the header). `wanted` is the code's length. Pure. */
export function spokenDigitSequenceOf(text, language, wanted) {
    if (!Number.isSafeInteger(wanted) || wanted < 1)
        throw new RangeError("a spoken digit sequence has a positive length");
    const primary = primarySubtagOf(language);
    const words = numberWordsOf(primary);
    if (words === null)
        return NONE;
    // A language with a word table has an entry in each of the three below (the suite pins it).
    const article = new Set(SPOKEN_DIGIT_ARTICLE[primary]);
    const zeroAlias = new Set(SPOKEN_DIGIT_ZERO_ALIAS[primary]);
    const repeat = SPOKEN_DIGIT_REPEAT_WORD[primary];
    // English says its hundreds as two words ("four hundred"); the other tables name each hundred as one word.
    const multiplier = Object.keys(words.hundred).length === 1;
    const isNumeral = (word) => /^\d+$/u.test(word);
    const isJoiner = (word) => words.joiner.includes(word);
    const digitOf = (word) => {
        if (article.has(word))
            return null;
        if (zeroAlias.has(word))
            return "0";
        const value = words.unit[word];
        return value !== undefined && value <= 9 ? String(value) : null;
    };
    const twoDigitOf = (word) => {
        if (article.has(word))
            return null;
        const value = words.unit[word];
        return value !== undefined && value >= 10 ? String(value) : null;
    };
    const isLexical = (word) => isNumeral(word) ||
        digitOf(word) !== null ||
        twoDigitOf(word) !== null ||
        words.ten[word] !== undefined ||
        words.hundred[word] !== undefined ||
        words.thousand[word] !== undefined ||
        repeat[word] !== undefined ||
        isJoiner(word);
    const piece = piecesOf(text);
    // Runs: maximal stretches of lexical pieces, joiners trimmed off both edges.
    const runs = [];
    const unknown = [];
    let open = null;
    const close = () => {
        if (open === null)
            return;
        let { first, last } = open;
        while (first <= last && isJoiner(piece[first].normal))
            first += 1;
        while (last >= first && isJoiner(piece[last].normal))
            last -= 1;
        if (first <= last)
            runs.push({ first, last });
        open = null;
    };
    piece.forEach((each, index) => {
        if (isLexical(each.normal)) {
            if (open === null)
                open = { first: index, last: index };
            else
                open.last = index;
            return;
        }
        close();
        if (runs.length > 0 && /^\p{L}+$/u.test(each.normal))
            unknown.push(each.normal);
    });
    close();
    // A digit run holds a numeral, or at least two number words: a lone number word is ordinary speech (see the header).
    const bearing = runs.filter((run) => {
        const inRun = piece.slice(run.first, run.last + 1);
        if (inRun.some((each) => isNumeral(each.normal)))
            return true;
        return inRun.filter((each) => !isJoiner(each.normal)).length >= 2;
    });
    if (bearing.length === 0)
        return NONE;
    const spanOf = (run) => Object.freeze({ start: piece[run.first].start, end: piece[run.last].end });
    const everySpan = Object.freeze(bearing.map(spanOf));
    const frozenUnknown = Object.freeze(unknown);
    if (bearing.length > 1) {
        return Object.freeze({
            reading: Object.freeze({ kind: "ambiguous" }),
            span: everySpan[0],
            everySpan,
            unknownToken: frozenUnknown,
        });
    }
    const run = bearing[0];
    const token = piece.slice(run.first, run.last + 1).map((each) => each.normal);
    const memo = new Map();
    const nextNonJoiner = (index) => {
        let at = index;
        while (at < token.length && isJoiner(token[at]))
            at += 1;
        return at;
    };
    /** One- and two-digit remainders after a hundreds word: `[digits, next index]`. */
    const remainderAt = (index) => {
        const at = nextNonJoiner(index);
        const word = token[at];
        if (word === undefined)
            return [];
        const result = [];
        const digit = digitOf(word);
        const two = twoDigitOf(word);
        const ten = words.ten[word];
        if (digit !== null && digit !== "0")
            result.push([`0${digit}`, at + 1]);
        if (two !== null)
            result.push([two, at + 1]);
        if (ten !== undefined) {
            result.push([String(ten), at + 1]);
            const after = nextNonJoiner(at + 1);
            const unit = token[after] === undefined ? null : digitOf(token[after]);
            if (unit !== null && unit !== "0")
                result.push([`${String(ten / 10)}${unit}`, after + 1]);
        }
        return result;
    };
    /** Every way to read the unit starting at `index`: `[digits, next index]`. A word with no reading has none. */
    const stepsAt = (index) => {
        const word = token[index];
        if (isJoiner(word))
            return [["", index + 1]];
        if (isNumeral(word))
            return [[word, index + 1]];
        const steps = [];
        const digit = digitOf(word);
        const two = twoDigitOf(word);
        const ten = words.ten[word];
        const hundred = words.hundred[word];
        const times = repeat[word];
        if (digit !== null) {
            steps.push([digit, index + 1]);
            const nextWord = token[index + 1];
            if (multiplier && digit !== "0" && nextWord !== undefined && words.hundred[nextWord] === 100) {
                steps.push([`${digit}00`, index + 2]);
                for (const [rest, next] of remainderAt(index + 2))
                    steps.push([`${digit}${rest}`, next]);
            }
        }
        if (two !== null)
            steps.push([two, index + 1]);
        if (ten !== undefined) {
            steps.push([String(ten), index + 1]);
            const after = nextNonJoiner(index + 1);
            const unit = token[after] === undefined ? null : digitOf(token[after]);
            if (unit !== null && unit !== "0")
                steps.push([`${String(ten / 10)}${unit}`, after + 1]);
        }
        if (hundred !== undefined && !multiplier) {
            steps.push([String(hundred), index + 1]);
            for (const [rest, next] of remainderAt(index + 1))
                steps.push([`${String(hundred / 100)}${rest}`, next]);
        }
        if (times !== undefined && token[index + 1] !== undefined) {
            const repeated = digitOf(token[index + 1]);
            if (repeated !== null)
                steps.push([repeated.repeat(times), index + 2]);
        }
        return steps;
    };
    const parse = (index) => {
        if (index >= token.length)
            return { fit: new Set([""]), over: false, saturated: false };
        const known = memo.get(index);
        if (known !== undefined)
            return known;
        const fit = new Set();
        let over = false;
        let saturated = false;
        for (const [digits, next] of stepsAt(index)) {
            const rest = parse(next);
            if (rest.over)
                over = true;
            if (rest.saturated)
                saturated = true;
            for (const tail of rest.fit) {
                const joined = `${digits}${tail}`;
                if (joined.length > wanted)
                    over = true;
                else if (fit.size < CANDIDATE_MAXIMUM)
                    fit.add(joined);
                else if (!fit.has(joined))
                    saturated = true;
            }
        }
        const parsed = { fit, over, saturated };
        memo.set(index, parsed);
        return parsed;
    };
    const top = parse(0);
    const exact = [...top.fit].filter((one) => one.length === wanted);
    const shorter = [...top.fit].filter((one) => one.length > 0 && one.length < wanted);
    const reading = top.saturated
        ? { kind: "ambiguous" }
        : exact.length === 1
            ? { kind: "exact", digits: exact[0] }
            : exact.length > 1
                ? { kind: "ambiguous" }
                : shorter.length > 0
                    ? { kind: "short", candidates: Object.freeze(shorter) }
                    : top.over
                        ? { kind: "long" }
                        : { kind: "ambiguous" };
    return Object.freeze({
        reading: Object.freeze(reading),
        span: everySpan[0],
        everySpan,
        unknownToken: frozenUnknown,
    });
}
//# sourceMappingURL=spoken-digit-sequence.js.map